import { type Slice } from "prosemirror-model";
import { type EditorView } from "prosemirror-view";
import { useCallback } from "react";

import { hydrateDropComponentTemplate } from "~/components/common/dnd";

import { usePropertiesPane } from "../easel/properties-pane/usePropertiesPane";
import { useEaselInsertTemplateAtPos } from "./useEaselInsertTemplateAtPos";

/**
 * Check if a DragEvent contains files (e.g., images dragged from the file system).
 * We return false for file drops so the imageDropPlugin can handle them instead.
 * On Linux/Tauri, files are passed as text/uri-list instead of Files.
 */
function hasFiles(e: DragEvent): boolean {
	const dataTransfer = e.dataTransfer;
	if (!dataTransfer) return false;

	// Check if there are files in the dataTransfer
	if (dataTransfer.files && dataTransfer.files.length > 0) {
		return true;
	}

	// Check the types array for "Files" which indicates a file drop
	if (dataTransfer.types.includes("Files")) {
		return true;
	}

	// On Linux/Tauri, files are passed as text/uri-list instead of Files
	if (dataTransfer.types.includes("text/uri-list")) {
		return true;
	}

	return false;
}

/**
 * Returns a callback intended to be passed to the `handleDrop` prop of the NYT `ProseMirror`
 * component. This callback will handle the drag-and-drop semantics for the editor, including
 * inserting templates.
 *
 * For file drops (e.g., images from the file system), this returns false to allow the
 * imageDropPlugin to handle them instead.
 */
export const useEaselProseMirrorDropHandler = () => {
	const insertTemplateAtPos = useEaselInsertTemplateAtPos();
	const { closePane } = usePropertiesPane();

	return useCallback(
		(view: EditorView, e: DragEvent, _slice: Slice, _moved: boolean): boolean => {
			// Let the imageDropPlugin handle file drops (e.g., images from file system)
			if (hasFiles(e)) {
				return false;
			}

			const template = hydrateDropComponentTemplate(e);
			const insertPos = view.posAtCoords({ left: e.clientX, top: e.clientY });

			if (template && insertPos) {
				const tr = insertTemplateAtPos(view.state.tr, template, insertPos.pos);
				view.dispatch(tr);
				view.focus();
				closePane();
				return true;
			}

			// No template found, let ProseMirror handle default behavior
			return false;
		},
		[closePane, insertTemplateAtPos]
	);
};
